import { Input as AntInput, type InputRef } from 'antd';
import React, { forwardRef, useId } from 'react';
import styled from 'styled-components';
import { IconWrap } from '../../icons/icon';
import { theme } from '../../theme/semanticTokens';
import { IconSize } from '../../types/icons/Icon.types';
import { IconType } from '../../types/icons/IconTypes.enum';
import {
  InputClearAction,
  type InputFieldBaseProps,
  inputFieldBorderStyle,
  InputHelpText,
  InputLabel
} from '../_shared/InputField';

const { Search: AntSearch } = AntInput;

export type SearchInputProps = InputFieldBaseProps &
  Partial<{
    onSearch: (value: string) => void;
  }>;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ForwardedAntSearch = forwardRef<InputRef, any>((props, ref) => (
  <AntSearch {...props} ref={ref} />
));

const StyledSearch = styled(ForwardedAntSearch)<{
  $error: boolean;
  $disabled: boolean;
  $readOnly: boolean;
}>`
  &&.ant-input-search {
    margin-bottom: ${theme.dime.spacing.m};

    .ant-input-affix-wrapper {
      ${inputFieldBorderStyle}
      border-radius: ${theme.dime.radius.m} 0 0 ${theme.dime.radius.m};
      padding: 0 ${theme.dime.spacing.l};
      height: ${theme.dime.spacing['3xl']};
      box-shadow: none;
    }

    .ant-input {
      height: auto;
      padding: 0;
      border: none;
      background: none;
      box-shadow: none;
    }

    .ant-input-clear-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: ${IconSize.Small};
      height: ${IconSize.Small};
      line-height: 1;
    }

    .ant-input:disabled,
    .ant-input-affix-wrapper-disabled {
      cursor: not-allowed;
    }

    .ant-input:disabled::placeholder {
      color: ${theme.dime.colors.text.disabled};
    }

    .ant-input[readonly] {
      cursor: not-allowed;
    }

    .ant-input-group-addon {
      background: none;
      border: none;
      padding: 0;
    }

    .ant-input-search-button {
      height: ${theme.dime.spacing['3xl']};
      width: ${theme.dime.spacing['3xl']};
      padding: 0;
      border: none;
      border-radius: 0 ${theme.dime.radius.m} ${theme.dime.radius.m} 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: ${({ $disabled, $readOnly }) =>
        $disabled || $readOnly
          ? theme.dime.colors.action.primaryDisabled
          : theme.dime.colors.action.primary};
      pointer-events: ${({ $readOnly }) => ($readOnly ? 'none' : 'auto')};

      &:hover {
        background-color: ${({ $disabled, $readOnly }) =>
          $disabled || $readOnly
            ? theme.dime.colors.action.primaryDisabled
            : theme.dime.colors.action.primaryHover};
      }

      &:disabled {
        background-color: ${theme.dime.colors.action.primaryDisabled};
        cursor: not-allowed;
      }
    }
  }
`;

export const SearchInput = forwardRef<InputRef, SearchInputProps>(
  (
    {
      value,
      defaultValue,
      placeholder = 'Search',
      disabled = false,
      readOnly = false,
      onChange,
      onSearch,
      className = '',
      id,
      name,
      'data-testid': testId,
      status = 'default',
      label,
      optional = false,
      infoIcon = false,
      infoText,
      helpText,
      clearAction = false,
      onClear,
      'aria-labelledby': ariaLabelledBy,
      ...rest
    },
    ref
  ) => {
    const hasError = status === 'error';
    const isButtonInactive = disabled || readOnly;
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const labelId = `${inputId}-label`;

    return (
      <InputWrapper className={className}>
        {!!label && (
          <InputLabel
            id={labelId}
            label={label}
            optional={optional}
            infoIcon={infoIcon}
            infoText={infoText}
          />
        )}
        <StyledSearch
          ref={ref}
          value={value}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          onChange={onChange}
          onSearch={readOnly ? undefined : onSearch}
          id={inputId}
          aria-labelledby={label ? labelId : ariaLabelledBy}
          name={name}
          data-testid={testId ?? 'search-input'}
          allowClear={{
            clearIcon: <IconWrap type={IconType.Close} size={IconSize.Small} />
          }}
          enterButton={
            <button
              type="button"
              className="ant-input-search-button"
              disabled={isButtonInactive}
              aria-label="Search"
            >
              <IconWrap
                type={IconType.Search}
                size={IconSize.Small}
                disabled={isButtonInactive}
                aria-hidden="true"
                style={
                  isButtonInactive
                    ? undefined
                    : { color: theme.dime.colors.icon.inverse }
                }
              />
            </button>
          }
          $error={hasError}
          $disabled={disabled}
          $readOnly={readOnly}
          {...rest}
        />
        {!!helpText && <InputHelpText text={helpText} hasError={hasError} />}
        {!!clearAction && !disabled && !readOnly && (
          <InputClearAction onClear={onClear} />
        )}
      </InputWrapper>
    );
  }
);

SearchInput.displayName = 'SearchInput';
